/**
 * ElasticsearchDemoPanel — C053 Elasticsearch 전문 검색 라이브 데모
 * 계약: docs/common-specs/eraf-data-elasticsearch/C053-elasticsearch.md §Δ.3 / §Δ.6
 * C055(KafkaDemoPanel)/C057(RabbitDemoPanel) 동형 패턴 — 단 왕복 UI 는 다르다:
 *   status 카드 → 색인 폼(message) → 검색 폼(query · 빈값=전체) → 히트 목록.
 * 5상태: loading(상태조회 skeleton) / empty(색인 전 · 검색 전 · 히트 0건) /
 *        error(상태조회 실패 · 색인 실패 · 검색 실패) / default(상태 + 색인결과 + 히트목록) /
 *        dialog(N/A — 인라인 콘솔, Kafka/RabbitMQ 동형)
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useElasticsearchStatus, useElasticsearchIndex, useElasticsearchSearch } from '../hooks/useElasticsearchDemo';
import {
  elasticsearchIndexFormSchema,
  elasticsearchSearchFormSchema,
  type ElasticsearchIndexFormValues,
  type ElasticsearchSearchFormValues,
} from '../schema/showcaseSchema';
import type { ApiError } from '../types/showcase';

export function ElasticsearchDemoPanel() {
  const { t } = useTranslation();
  const statusQuery = useElasticsearchStatus();
  const indexMutation = useElasticsearchIndex();
  const searchMutation = useElasticsearchSearch();

  const [indexError, setIndexError] = useState<ApiError | null>(null);
  const [searchError, setSearchError] = useState<ApiError | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const {
    register: registerIndex,
    handleSubmit: handleIndexSubmit,
    reset: resetIndex,
    formState: { errors: indexErrors, isValid: isIndexValid },
  } = useForm<ElasticsearchIndexFormValues>({
    resolver: zodResolver(elasticsearchIndexFormSchema),
    defaultValues: { message: '' },
  });

  const {
    register: registerSearch,
    handleSubmit: handleSearchSubmit,
    getValues: getSearchValues,
    formState: { errors: searchErrors },
  } = useForm<ElasticsearchSearchFormValues>({
    resolver: zodResolver(elasticsearchSearchFormSchema),
    defaultValues: { query: '' },
  });

  // 색인 성공 후 자동 1회 검색 — 왕복을 가시화 (빈 query = 전체 조회로 방금 색인분 확인)
  useEffect(() => {
    if (!indexMutation.isSuccess) return;
    setSearchError(null);
    setHasSearched(true);
    searchMutation.mutate(
      { query: getSearchValues('query') ?? '' },
      { onError: (err) => setSearchError(err) }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexMutation.data]);

  const status = statusQuery.data;
  const isStatusLoading = statusQuery.isLoading;
  const isStatusError = statusQuery.isError;
  const disabled = !status?.enabled;

  function onIndexSubmit(values: ElasticsearchIndexFormValues) {
    setIndexError(null);
    indexMutation.mutate(
      { message: values.message },
      {
        onSuccess: () => resetIndex({ message: '' }),
        onError: (err) => setIndexError(err),
      }
    );
  }

  function onSearchSubmit(values: ElasticsearchSearchFormValues) {
    setSearchError(null);
    setHasSearched(true);
    searchMutation.mutate(
      { query: values.query },
      { onError: (err) => setSearchError(err) }
    );
  }

  return (
    <section aria-labelledby="elasticsearch-panel-title">
      <h2
        id="elasticsearch-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.elasticsearch.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.elasticsearch.subtitle')}
      </p>

      {/* status — loading skeleton */}
      {isStatusLoading && (
        <div aria-busy="true" aria-label={t('showcase.elasticsearch.status.loadingAriaLabel')} style={cardStyle}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 16,
                width: `${35 + ((i * 15) % 40)}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
                marginBottom: 'var(--spacing-2)',
              }}
            />
          ))}
        </div>
      )}

      {/* status — error */}
      {isStatusError && (
        <div role="alert" style={{ ...cardStyle, border: '1px solid var(--color-danger)' }}>
          <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {(statusQuery.error as ApiError)?.message ?? t('showcase.elasticsearch.status.loadFailed')}
          </p>
          <button type="button" onClick={() => void statusQuery.refetch()} style={secondaryButtonStyle}>
            {t('showcase.common.retry')}
          </button>
        </div>
      )}

      {/* status — default */}
      {!isStatusLoading && !isStatusError && status && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.elasticsearch.status.title')}</h3>
            <div style={infoGridStyle}>
              <InfoField label={t('showcase.elasticsearch.status.enabled')}>
                <span style={{ color: status.enabled ? 'var(--color-status-success)' : 'var(--color-status-error)', fontWeight: 700 }}>
                  {status.enabled ? t('showcase.elasticsearch.status.enabledYes') : t('showcase.elasticsearch.status.enabledNo')}
                </span>
              </InfoField>
              <InfoField label={t('showcase.elasticsearch.status.clusterInfo')}>
                <code style={monoStyle}>{status.clusterInfo}</code>
              </InfoField>
              <InfoField label={t('showcase.elasticsearch.status.indexName')}>
                <code style={monoStyle}>{status.indexName}</code>
              </InfoField>
              <InfoField label={t('showcase.elasticsearch.status.docCount')}>
                <code style={monoStyle}>{status.docCount}</code>
              </InfoField>
            </div>
            {!status.enabled && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)' }}>
                {t('showcase.elasticsearch.disabledNotice')}
              </p>
            )}
          </div>

          {/* index */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.elasticsearch.index.title')}</h3>
            <form onSubmit={handleIndexSubmit(onIndexSubmit)} noValidate style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 240px' }}>
                <input
                  id="elasticsearch-message"
                  type="text"
                  placeholder={t('showcase.elasticsearch.index.placeholder')}
                  aria-label={t('showcase.elasticsearch.index.title')}
                  aria-invalid={!!indexErrors.message}
                  aria-describedby={indexErrors.message ? 'elasticsearch-message-error' : undefined}
                  disabled={disabled}
                  {...registerIndex('message')}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-3)',
                    border: `1px solid ${indexErrors.message ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontFamily: 'var(--font-family-sans)',
                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
                    color: 'var(--color-text-primary)',
                    boxSizing: 'border-box',
                  }}
                />
                {indexErrors.message && (
                  <p id="elasticsearch-message-error" role="alert" style={errorTextStyle}>
                    {indexErrors.message.message}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={disabled || !isIndexValid || indexMutation.isPending}
                aria-busy={indexMutation.isPending}
                style={{
                  ...primaryButtonStyle,
                  opacity: disabled || indexMutation.isPending ? 0.6 : 1,
                  cursor: disabled || indexMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {indexMutation.isPending ? t('showcase.elasticsearch.index.sending') : t('showcase.elasticsearch.index.button')}
              </button>
            </form>

            {/* index — error */}
            {indexError && !indexMutation.isPending && (
              <div role="alert" style={{ marginTop: 'var(--spacing-3)', ...errorBoxStyle }}>
                {indexError.message ?? t('showcase.elasticsearch.index.error')}
              </div>
            )}

            {/* index — empty (아직 색인 전) */}
            {!indexMutation.data && !indexError && !indexMutation.isPending && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('showcase.elasticsearch.index.empty')}
              </p>
            )}

            {/* index — default (결과) */}
            {indexMutation.data && !indexMutation.isPending && (
              <div
                role="region"
                aria-label={t('showcase.elasticsearch.index.resultLabel')}
                style={{ marginTop: 'var(--spacing-4)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--spacing-3)' }}
              >
                <InfoField label={t('showcase.elasticsearch.index.result.index')}>
                  <code style={monoStyle}>{indexMutation.data.index}</code>
                </InfoField>
                <InfoField label={t('showcase.elasticsearch.index.result.docId')}>
                  <code style={monoStyle}>{indexMutation.data.docId}</code>
                </InfoField>
                <InfoField label={t('showcase.elasticsearch.index.result.indexedAt')}>
                  <code style={monoStyle}>{indexMutation.data.indexedAt}</code>
                </InfoField>
              </div>
            )}
          </div>

          {/* search */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.elasticsearch.search.title')}</h3>
            <form onSubmit={handleSearchSubmit(onSearchSubmit)} noValidate style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 240px' }}>
                <input
                  id="elasticsearch-query"
                  type="text"
                  placeholder={t('showcase.elasticsearch.search.placeholder')}
                  aria-label={t('showcase.elasticsearch.search.title')}
                  aria-invalid={!!searchErrors.query}
                  aria-describedby={searchErrors.query ? 'elasticsearch-query-error' : undefined}
                  disabled={disabled}
                  {...registerSearch('query')}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-3)',
                    border: `1px solid ${searchErrors.query ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontFamily: 'var(--font-family-sans)',
                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
                    color: 'var(--color-text-primary)',
                    boxSizing: 'border-box',
                  }}
                />
                {searchErrors.query && (
                  <p id="elasticsearch-query-error" role="alert" style={errorTextStyle}>
                    {searchErrors.query.message}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={disabled || searchMutation.isPending}
                aria-busy={searchMutation.isPending}
                style={{
                  ...primaryButtonStyle,
                  opacity: disabled || searchMutation.isPending ? 0.6 : 1,
                  cursor: disabled || searchMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {searchMutation.isPending ? t('showcase.elasticsearch.search.searching') : t('showcase.elasticsearch.search.button')}
              </button>
            </form>

            {/* search — error */}
            {searchError && !searchMutation.isPending && (
              <div role="alert" style={{ marginTop: 'var(--spacing-3)', ...errorBoxStyle }}>
                {searchError.message ?? t('showcase.elasticsearch.search.error')}
              </div>
            )}

            {/* search — empty (아직 검색 전 또는 히트 0건) */}
            {!searchMutation.isPending && !searchError && (!hasSearched || !searchMutation.data || searchMutation.data.hits.length === 0) && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('showcase.elasticsearch.search.empty')}
              </p>
            )}

            {/* search — default (히트 목록) */}
            {!searchMutation.isPending && searchMutation.data && searchMutation.data.hits.length > 0 && (
              <div style={{ marginTop: 'var(--spacing-4)' }}>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-2)' }}>
                  {t('showcase.elasticsearch.search.total', { count: searchMutation.data.total })}
                </p>
                <div role="table" aria-label={t('showcase.elasticsearch.search.tableLabel')} style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-xs)' }}>
                    <thead>
                      <tr>
                        {[
                          t('showcase.elasticsearch.search.col.message'),
                          t('showcase.elasticsearch.search.col.score'),
                          t('showcase.elasticsearch.search.col.sentBy'),
                          t('showcase.elasticsearch.search.col.sentAt'),
                        ].map((h) => (
                          <th
                            key={h}
                            scope="col"
                            style={{
                              padding: 'var(--spacing-2)',
                              textAlign: 'left',
                              borderBottom: '1px solid var(--color-surface-border)',
                              color: 'var(--color-text-secondary)',
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {searchMutation.data.hits.map((hit, idx) => (
                        <tr key={`${hit.docId}-${idx}`}>
                          <td style={tdStyle}>{hit.message}</td>
                          <td style={{ ...tdStyle, fontFamily: 'var(--font-family-mono)' }}>{hit.score.toFixed(2)}</td>
                          <td style={{ ...tdStyle, fontFamily: 'var(--font-family-mono)' }}>{hit.sentBy}</td>
                          <td style={tdStyle}>{hit.sentAt}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Learning */}
      <div
        style={{
          marginTop: 'var(--spacing-4)',
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.elasticsearch.learning')}
      </div>
    </section>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>{label}</p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>{children}</div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  padding: 'var(--spacing-5)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-background)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-4)',
};

const infoGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 'var(--spacing-4)',
};

const monoStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-sm)',
};

const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  fontFamily: 'var(--font-family-sans)',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const errorTextStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};

const errorBoxStyle: React.CSSProperties = {
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-danger)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-muted)',
  color: 'var(--color-danger)',
  fontSize: 'var(--font-size-sm)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2)',
  borderBottom: '1px solid var(--color-surface-border)',
  color: 'var(--color-text-primary)',
};
